/* istanbul ignore file */
import { EnhancedStore } from '@reduxjs/toolkit';
import axios, { AxiosError, AxiosInstance, InternalAxiosRequestConfig } from 'axios';
import config from 'environment';
import { History } from 'history';
import { Problem } from 'types/entity';
import { reportError, setError } from './error.handler';

/**
 * アクセスポイントが必要なAPIで使用するヘッダー.<br>
 * 必要に応じて各Serviceで使用してください
 */
const headerAccessPoint = { AccessPoint: 'FILE_PORTAL' } as const;

// ファイル管理ポータルのAxios
const http: AxiosInstance = axios.create({ baseURL: config.resourceServer });
http.defaults.timeout = parseInt(config.defaultResourceTimeout, 10);

// 図面管理のAxios
const httpByDdms: AxiosInstance = axios.create({ baseURL: config.resourceServerByDdms });
httpByDdms.defaults.timeout = parseInt(config.defaultResourceTimeout, 10);

const interceptor = (store: EnhancedStore, history: History): void => {
  // ファイル管理ポータルAPIの初期設定
  http.interceptors.request.use(
    (_config: InternalAxiosRequestConfig) => {
      _config.headers['X-Requested-With'] = 'XMLHttpRequest';
      _config.headers.Authorization = `Bearer ${store.getState().userRead.accessToken}`;
      _config.headers['xgrab-access-point'] = 'FILE_PORTAL';
      return _config;
    },
    (error: any) => Promise.reject(error),
  );
  http.interceptors.response.use(
    (res: any) => res,
    (error: AxiosError<Problem>) => {
      const statusCode = error.response?.status || 500; // 不明は500で扱う.
      // 400台のエラーは送信しない
      if (statusCode < 400 || statusCode >= 500) {
        const report = { ...(error.toJSON() as httpResponseErrorReport), response: { ...error.response }, request: { ...error.request } } as httpResponseErrorReport;
        try {
        // Authorization ヘッダを送信しないようマスクする.
          report.config.headers.Authorization = '***** masked *****';
          report.response.config.headers.Authorization = '***** masked *****';
        } catch (e) {
        // プロパティが取得できない場合にエラーが起きるが無視する.
        }
        reportError(report);
      }
      setError(error.response?.data, store, history);
      if (process.env.NODE_ENV === 'development') {
        console.error('api request error', error.response?.data);
      }
      return Promise.reject(error);
    },
  );

  // 図面管理APIの初期設定
  httpByDdms.interceptors.request.use(
    (_config: InternalAxiosRequestConfig) => {
      _config.headers['X-Requested-With'] = 'XMLHttpRequest';
      _config.headers.Authorization = `Bearer ${store.getState().userRead.accessToken}`;
      _config.headers['xgrab-access-point'] = headerAccessPoint.AccessPoint;
      return _config;
    },
    (error: any) => Promise.reject(error),
  );
  httpByDdms.interceptors.response.use(
    (res: any) => res,
    (error: AxiosError<Problem>) => {
      setError(error.response?.data, store, history);
      if (process.env.NODE_ENV === 'development') {
        console.error('ddms api request error', error.response?.data);
      }
      return Promise.reject(error);
    },
  );
};

/**
 * Authorizationヘッダのマスク用
 */
interface httpResponseErrorReport {
    config: {
        headers: {
            Authorization?: string
        }
    }
    response: {
        config: {
            headers: {
                Authorization?: string
            }
        }
    },
    request: any
}

export {
  interceptor,
  http,
  httpByDdms,
  headerAccessPoint,
};
